export const dealCreationTypeMethods = {
  setDealCreationType (creationType) {
    const dealId = this.deal.id

    // When the deal creation type is selected
    // the deal should be cleared
    this.clearDeal()

    // However, we keep the deal id, so the next time
    // the user saves the deal,
    // it updates the existing one instead of creating a new one
    this.deal.id = dealId
    this.deal.dealCreationTypeId = creationType
  },
  async handleImportFromZoho (jobOpeningInfo) {
    // Clear the deal before populating the wizard with the
    // information from the Job Opening
    this.clearDeal()

    // Set the Job Opening ID
    this.deal.zrJobOpeningId = jobOpeningInfo.id
    // Keep the Job Opening information
    this.jobOpeningInfo = jobOpeningInfo

    // Set the deal creation type to "Import from Zoho"
    this.deal.dealCreationTypeId = this.CREATE_FROM_ZOHO

    // Populate the wizard with the information from the Job Opening
    await this.populateAssignmentType(jobOpeningInfo)
    this.populateClient(jobOpeningInfo)
    this.populateCandidate(jobOpeningInfo)
    this.populateContractDetails(jobOpeningInfo)

    // Mark all steps as completed so the user can navigate through all the steps
    this.markAllStepsAsCompleted()

    // Set the current step to summary so the user
    // can review all the imported information
    this.currentStep = this.SUMMARY
  }
}

export const zohoImportMethods = {
  async populateAssignmentType (jobOpeningInfo) {
    if (jobOpeningInfo.job_Type.toLowerCase().includes('full') ||
      jobOpeningInfo.job_Type.toLowerCase().includes('perm')) {
      await this.setDealAssignmentType(this.PERMANENT_PLACEMENT)
    } else {
      await this.setDealAssignmentType(this.CONTRACTOR)
    }
  },
  populateClient (jobOpeningInfo) {
    this.getClients().then((clientList) => {
      // Get all the clients and find their names similarity against the
      // name provided in the jobOpeningInfo
      let clientListTemp = [...clientList]
      clientListTemp = clientListTemp.map(obj => ({
        ...obj,
        similarity: this.wordsSimilarity(obj.name, jobOpeningInfo.client_Name.name)
      }))
      // Choose the client with the highest similarity
      let maxSimilarity = Math.max(...clientListTemp.map(obj => obj.similarity))
      let tempClient = clientListTemp.find(obj => obj.similarity === maxSimilarity)
      // If the similarity percentage is greater than 80% suggest this client as an existing one.
      if (tempClient.similarity >= 0.8) {
        this.setExistingClientId(tempClient.id)
      } else {
        // Otherwise suggest the client provided by jobOpeningInfo as a new client.
        this.setClientName(jobOpeningInfo.client_Name.name)
      }
    })
  },
  populateCandidate (jobOpeningInfo) {
    const candidateId = this.deal.candidates[0].id
    // Get all the users in the system and find their names similarity
    // against the name provided in the jobOpeningInfo
    if (jobOpeningInfo.associated_Candidates && jobOpeningInfo.associated_Candidates.length > 0) {
      const zohoCandidate = jobOpeningInfo.associated_Candidates[0]
      this.getUsers().then((userList) => {
        let tempUsersList = [...userList]
        tempUsersList = tempUsersList.map(obj => ({
          ...obj,
          similarity: this.wordsSimilarity(
            obj.fullName,
            zohoCandidate.full_Name)
        }))
        // Choose the user with the highest similarity
        let maxSimilarity = Math.max(...tempUsersList.map(obj => obj.similarity))
        let tempUser = tempUsersList.find(obj => obj.similarity === maxSimilarity)
        // If the similarity percentage is greater than 80% suggest this user as an existing one.
        if (tempUser.similarity >= 0.8) {
          this.setExistingCandidateId(candidateId, tempUser.id)
        } else {
          // Otherwise suggest the user provided by jobOpeningInfo as a new user.
          this.setFirstName(candidateId, zohoCandidate.first_Name)
          this.setLastName(candidateId, zohoCandidate.last_Name)
          this.setEmailAddress(candidateId, zohoCandidate.email)
        }
      })
    }
  },
  populateContractDetails (jobOpeningInfo) {
    const candidateId = this.deal.candidates[0].id
    if (this.deal.dealAssignmentType === this.CONTRACTOR) {
      if (jobOpeningInfo.hourly_Rate) {
        this.setPayRateTypeId(this.HOURLY_PAY_RATE, candidateId)
        this.setPayRate(jobOpeningInfo.hourly_Rate, candidateId)
      }
      if (jobOpeningInfo.client_Rate) {
        this.setBillRateTypeId(this.HOURLY_BILL_RATE, candidateId)
        this.setBillRate(jobOpeningInfo.client_Rate, candidateId)
      }
      if (!!jobOpeningInfo.duration && jobOpeningInfo.duration > 0 &&
          jobOpeningInfo.duration_Type === 'Months') {
        this.setContractTypeId(this.CONTRACT_TO_HIRE, candidateId)
        let length = parseInt(jobOpeningInfo.duration)
        this.setLengthOfContract(isNaN(length) ? 0 : length, candidateId)
      }
    }
    this.setJobTitle(jobOpeningInfo.posting_Title, candidateId)
  }
}

export const dealAssignmentTypeMethods = {
  async setDealAssignmentType (dealType) {
    // The Project type supports multiple candidates, while Contractor and Permanent Placement
    // only support a single candidate. When switching away from Project, remove all candidates
    // except the first one to ensure the deal is in a valid state for the new type.
    if (this.deal.dealAssignmentType === this.PROJECT && dealType !== this.PROJECT) {
      if (Array.isArray(this.deal.candidates) && this.deal.candidates.length > 1) {
        const candidatesToRemove = this.deal.candidates.slice(1)
        for (const candidate of candidatesToRemove) {
          await this.deleteCandidate(candidate.id)
        }
      }
    }

    this.$set(this.deal, 'dealAssignmentType', dealType)

    if (dealType !== this.PERMANENT_PLACEMENT) {
      // The Contractor and Project type require the payRateSettings, workedHoursAverageDefault
      // overtimeThresholdDefault and overtimeRateDefault values from the settings
      // to perform some calculations on the contract details step.

      // The fetching happens here because otherwise the UI doesn't refresh the values
      // properly.
      await this.getSettings()
    }

    this.nextStepAndSave()
  }
}

export const dealClientMethods = {
  updateClient (propName, value) {
    this.$set(this.deal.client, propName, value)
  },

  setClientTypeId (clientTypeId) {
    this.updateClient('clientTypeId', clientTypeId)
    this.clearClientData()
  },

  setClientName (clientName) {
    this.updateClient('name', clientName)
  },

  setBusinessEntityId (businessEntityId) {
    this.clearClientData()
    this.updateClient('businessEntityId', businessEntityId)
  },

  setExistingClientId (existingClientId) {
    this.updateClient('existingClientId', existingClientId)

    if (!existingClientId) return

    this.getClient({ id: existingClientId }).then(clientData => {
      this.updateClient('businessEntityId', clientData.businessEntityId)
      this.updateClient('name', clientData.name)
      this.updateClient('invoiceFrequencyId', clientData.invoiceFrequencyId)
      this.updateClient('thirdPartyPortalId', clientData.thirdPartyPortalId)
      this.updateClient('vmsFee', clientData.vmsFee)
      this.updateClient('useStaffLynxForTimeEntering', clientData.useStaffLynxForTimeEntering)
    })
  },

  setInvoiceFrequencyId (invoiceFrequencyId) {
    this.updateClient('invoiceFrequencyId', invoiceFrequencyId)
  },

  setThirdPartyPortalId (thirdPartyPortalId) {
    this.updateClient('thirdPartyPortalId', thirdPartyPortalId)
  },

  setVmsFee (vmsFee) {
    this.updateClient('vmsFee', vmsFee)
  },

  setUseStaffLynxForTimeEntering (useStaffLynxForTimeEntering) {
    this.updateClient('useStaffLynxForTimeEntering', useStaffLynxForTimeEntering)
  },

  clearClientData () {
    this.updateClient('name', null)
    this.updateClient('existingClientId', null)
    this.updateClient('invoiceFrequencyId', null)
    this.updateClient('thirdPartyPortalId', null)
    this.updateClient('vmsFee', null)
    this.updateClient('useStaffLynxForTimeEntering', false)
  }
}

export const dealProjectMethods = {
  updateProject (propName, value) {
    this.$set(this.deal.project, propName, value)
  },

  setProjectName (projectName) {
    this.updateProject('projectName', projectName)
  }
}

export const dealCandidateMethods = {
  updateCandidate (candidateId, updates) {
    if (
      !this.deal ||
      !Array.isArray(this.deal.candidates)
    ) return

    const index = this.deal.candidates.findIndex(
      c => c.id === candidateId
    )

    if (index === -1) return

    const updatedCandidate = {
      ...this.deal.candidates[index],
      ...updates
    }

    this.$set(this.deal.candidates, index, updatedCandidate)
  },

  setCandidateTypeId (candidateId, candidateTypeId) {
    this.updateCandidate(candidateId, { candidateTypeId })
    this.clearCandidate(candidateId)
  },

  setExistingCandidateId (candidateId, existingUserId) {
    this.updateCandidate(candidateId, { existingUserId })

    this.getUser({ id: existingUserId }).then(data => {
      this.updateCandidate(candidateId, {
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress
      })
    })
  },

  setFirstName (candidateId, firstName) {
    this.updateCandidate(candidateId, { firstName })
  },

  setLastName (candidateId, lastName) {
    this.updateCandidate(candidateId, { lastName })
  },

  setEmailAddress (candidateId, emailAddress) {
    this.updateCandidate(candidateId, { emailAddress })
  },

  clearCandidate (candidateId) {
    this.updateCandidate(candidateId, {
      existingUserId: null,
      firstName: null,
      lastName: null,
      emailAddress: null
    })
  },
  addCandidateHandler () {
    this.addCandidate()
  },

  deleteCandidateHandler (dealCandidateId) {
    this.deleteCandidate(dealCandidateId)
  }
}

export const dealContractDetailsMethods = {
  updateContract (candidateId, updates) {
    if (
      !this.deal ||
      !this.deal.candidates ||
      !Array.isArray(this.deal.candidates)
    ) {
      return
    }

    const index = this.deal.candidates.findIndex(
      c => c.id === candidateId
    )

    if (index === -1) return

    const candidate = this.deal.candidates[index]

    const updatedAssignment = {
      ...candidate.dealAssignment,
      ...updates
    }

    const updatedCandidate = {
      ...candidate,
      dealAssignment: updatedAssignment
    }

    this.$set(this.deal.candidates, index, updatedCandidate)
  },

  setJobTitle (value, id) {
    this.updateContract(id, { jobTitle: value })
  },

  setWorkedHoursAverage (value, id) {
    this.updateContract(id, { workedHoursAverage: value })
  },

  setPayFrequencyId (value, id) {
    this.updateContract(id, { payFrequencyId: value })
  },

  setContractTypeId (value, id) {
    this.updateContract(id, { contractTypeId: value })
  },

  setLengthOfContract (value, id) {
    this.updateContract(id, { lengthOfContract: value })
  },

  setConversionFee (value, id) {
    this.updateContract(id, { conversionFee: value })
  },

  setStartAt (value, id) {
    this.updateContract(id, { startAt: value })
  },

  setEndAt (value, id) {
    this.updateContract(id, { endAt: value })
  },

  setEmployeeTypeId (value, id) {
    this.updateContract(id, { employeeTypeId: value })
  },

  setCorporateName (value, id) {
    this.updateContract(id, { corporateName: value })
  },

  setEin (value, id) {
    this.updateContract(id, { ein: value })
  },

  setPayRate (value, id) {
    this.updateContract(id, { payRate: value })
  },

  setPayRateTypeId (value, id) {
    this.updateContract(id, { payRateTypeId: value })
  },

  setBillRate (value, id) {
    this.updateContract(id, { billRate: value })
  },

  setBillRateTypeId (value, id) {
    this.updateContract(id, { billRateTypeId: value })
  },

  setOvertimeEligible (value, id) {
    this.updateContract(id, { overtimeEligible: value })
  },

  setOvertimeThreshold (value, id) {
    this.updateContract(id, { overtimeThreshold: value })
  },

  setOvertimeRate (value, id) {
    this.updateContract(id, { overtimeRate: value })
  },

  setTimeSheetType (value, id) {
    this.updateContract(id, { timeSheetType: value })
  },

  setClockIPAddress (value, id) {
    this.updateContract(id, { clockIPAddress: value })
  },

  setCommissions (value, id) {
    this.updateContract(id, { commissions: value })
  },

  setExpenses (value, id) {
    this.updateContract(id, { expenses: value })
  },

  setContractorCommissionEnabled (value, id) {
    this.updateContract(id, { contractorCommissionEnabled: value })
  },

  setContractorCommissionMarkup (value, id) {
    this.updateContract(id, { contractorCommissionMarkup: value })
  }
}

export const dealPermPlacementMethods = {
  setPermPlacementDate (value, id) {
    this.updateContract(id, { permPlacementDate: value })
  },

  setPermPlacementSalary (value, id) {
    this.updateContract(id, { permPlacementSalary: value })
  },

  setPermPlacementFee (value, id) {
    this.updateContract(id, { permPlacementFee: value })
  },

  setPermPlacementFeeTypeId (value, id) {
    this.updateContract(id, { permPlacementFeeTypeId: value })
  },

  setPermPlacementInvoiceDate (value, id) {
    this.updateContract(id, { permPlacementInvoiceDate: value })
  },

  setPermPlacementInvoiceDueDate (value, id) {
    this.updateContract(id, { permPlacementInvoiceDueDate: value })
  }
}

export const dealSummaryMethods = {
  setRejectionComments (comments) {
    if (!this.deal.notes) {
      this.deal.notes = ''
    }
    this.deal.notes += comments + '<br>'
  }
}
